/**
 * The quick mission log's own store — Dexie's `quickDriverTasks` table.
 *
 * A quick task is filed in one tap from the dashboard, so this module is
 * deliberately small: one write, one read, one wipe. What it does *not* do is as
 * important as what it does — it never touches the board, never opens a task,
 * never binds a vehicle. A hand-logged errand is evidence for the workload
 * graph, not a dispatched mission, and mixing the two would put a journey nobody
 * ordered onto the daily dispatch sheet.
 *
 * Three things happen on every write, in this order:
 *
 * 1. The row lands in IndexedDB, with the driver's name and the operator's name
 *    frozen onto it (`src/core/audit-stamp.ts`'s rule).
 * 2. A ledger row is appended, so "מי רשם את המשימה הזאת" is answerable from the
 *    audit trail on any device and not just the one it was typed on.
 * 3. `emitStoreChange('tasks')` announces it — which repaints this window,
 *    carries the announcement to every other window on the device
 *    (`src/core/live-updates.ts`), and raises the cloud push signal so the row is
 *    on the other managers' phones within a moment rather than within a poll.
 *
 * The table rides the cloud mirror like any other synced record — it is on
 * `SYNC_TABLES` in `src/core/sync-engine.ts` — and the push endpoint additionally
 * projects it into D1's own `quick_driver_tasks` table
 * (`migrations/0004_quick_driver_tasks.sql`), so the log is queryable from the
 * database without unpacking JSON.
 *
 * Offline-first is untouched: with no backend behind it the write still lands,
 * the graph still moves, and the row goes up whenever the device next has signal.
 */
import { db } from './index';
import { recordActorEvent } from './audit-store';
import { assertPermission } from '../core/permissions';
import { actorName } from '../core/audit-stamp';
import {
  QUICK_TASK_REFUSALS,
  normalizeQuickDescription,
  quickTaskRefusal,
} from '../core/driver-workload';
import type { QuickTaskRefusal } from '../core/driver-workload';
import { soldierDisplayName } from '../core/soldier-roles';
import { emitStoreChange } from '../core/store-events';
import type { QuickDriverTask, QuickDriverTaskDraft } from '../types/quick-task';
import type { User } from '../types/user';

const nowIso = (now: number = Date.now()): string => new Date(now).toISOString();

const newQuickTaskId = (): string => `qtsk-${crypto.randomUUID()}`;

/** Raised when the two-field form was submitted incomplete. */
export class QuickTaskDraftError extends Error {
  readonly refusal: QuickTaskRefusal;

  constructor(refusal: QuickTaskRefusal) {
    super(QUICK_TASK_REFUSALS[refusal]);
    this.name = 'QuickTaskDraftError';
    this.refusal = refusal;
  }
}

/** Raised when the driver named on the form is not on the roster. */
export class UnknownDriverError extends Error {
  constructor(driverId: string) {
    super(`Unknown driver: ${driverId}`);
    this.name = 'UnknownDriverError';
  }
}

// ── Reads ────────────────────────────────────────────────────────────

/** Every logged mission, newest first — the order the audit strip reads them. */
export async function listQuickDriverTasks(): Promise<QuickDriverTask[]> {
  const rows = await db.quickDriverTasks.toArray();
  return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** One driver's own logged missions, newest first. */
export async function getDriverQuickTasks(driverId: string): Promise<QuickDriverTask[]> {
  const rows = await db.quickDriverTasks.where('driverId').equals(driverId).toArray();
  return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

// ── Write ────────────────────────────────────────────────────────────

/**
 * Files one quick mission.
 *
 * Guarded by `CREATE_TASK`: recording that a driver carried something out is the
 * same authority as opening the job in the first place, and a שדרן who may not
 * open a task may not credit one either.
 *
 * The driver is resolved against the roster rather than trusted from the form —
 * a name typed into an autocomplete is a suggestion until the roster confirms
 * it — and the name that is confirmed is frozen onto the row, so a device that
 * pulls this record before it pulls the roster still draws a labelled bar.
 */
export async function logQuickDriverTask(
  actor: User,
  draft: QuickDriverTaskDraft,
  now: number = Date.now(),
): Promise<QuickDriverTask> {
  assertPermission(actor, 'CREATE_TASK');

  const refusal = quickTaskRefusal(draft);
  if (refusal) throw new QuickTaskDraftError(refusal);

  const driverId = String(draft.driverId).trim();
  const driver = await db.soldiers.get(driverId);
  if (!driver) throw new UnknownDriverError(driverId);

  const stamp = nowIso(now);
  // `actorName()` never returns a blank — an account with no name on it is filed
  // as "—" — which is what lets {@link QuickDriverTask} insist on the field.
  const author = actorName(actor);
  const entry: QuickDriverTask = {
    id: newQuickTaskId(),
    driverId,
    driverName: String(driver.fullName ?? '').trim(),
    description: normalizeQuickDescription(draft.description),
    createdBy: actor.id,
    createdAt: stamp,
    updatedAt: stamp,
    createdByName: author,
    updatedByName: author,
  };

  await db.quickDriverTasks.add(entry);

  // The ledger row names the driver the way the unit does — "נתי כהן - נהג" —
  // because the audit log is read long after somebody has forgotten which id
  // belonged to whom.
  await recordActorEvent(actor, {
    action: 'QUICK_TASK_LOGGED',
    entityType: 'soldier',
    entityId: driverId,
    summary: `רישום משימה מהירה — ${soldierDisplayName(driver)}: ${entry.description}`,
    metadata: {
      quickTaskId: entry.id,
      driverId,
      driverName: entry.driverName,
      description: entry.description,
    },
    createdAt: stamp,
  });

  emitStoreChange('tasks');
  return entry;
}

// ── Maintenance ──────────────────────────────────────────────────────

/**
 * Wipes the quick log — the "reset local data" action reaches this through
 * `resetTaskData()` in `src/db/task-store.ts`, so no reset path has to remember
 * the table separately.
 *
 * Silent about the reactive store on purpose: the caller announces the wipe once
 * for everything it cleared, rather than each table announcing its own.
 */
export async function resetQuickDriverTasks(): Promise<void> {
  await db.quickDriverTasks.clear();
}
